import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { Get } from '../../Axios/AxiosFunctions';
import { BaseURL } from '../../config/apiUrl';
import classes from './DeadlineSchedulePreview.module.css';

/**
 * Shows the deadline schedule a template would produce, without writing it.
 *
 * This is the coordinator's check against the external calculator they used
 * to rebuild these dates by hand, so it renders every computed date with its
 * weekday and the reason any date moved. Shared by the Apply Template modal
 * and Add Project so the two can never drift apart.
 */
const DeadlineSchedulePreview = ({
  templateSlug,
  anchorDate,
  dayBasis = 'business',
  projectId,
  active = true,
  // When provided, each Deadline cell becomes editable and every hand-set
  // date is reported back as [{ templateTaskId, deadlineDate }].
  overrides = null,
  onOverridesChange = null,
}) => {
  const editable = typeof onOverridesChange === 'function';
  const { access_token: accessToken, company_id: companyId } = useSelector(
    (state) => state?.authReducer || {},
  );

  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Depend on the overrides' CONTENT, not the array's identity: a new array
  // each render would otherwise refetch forever.
  const overrideKey = JSON.stringify(
    [...(overrides || [])].sort((a, b) =>
      String(a.templateTaskId).localeCompare(String(b.templateTaskId)),
    ),
  );

  useEffect(() => {
    if (!active || !templateSlug || !anchorDate) {
      setPreview(null);
      setError('');
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError('');

    const params = new URLSearchParams({ anchorDate, dayBasis });
    if (projectId) params.set('projectId', projectId);
    if (overrides?.length) params.set('overrides', JSON.stringify(overrides));

    // Debounced, and showAlert=false: this re-fires as the coordinator edits
    // the date, so a failure must not raise a toast per keystroke. Get
    // resolves with undefined on error rather than rejecting.
    const timer = setTimeout(() => {
      Get(
        BaseURL(
          `projects/deadlines/preview/${templateSlug}?${params.toString()}`,
        ),
        accessToken,
        companyId,
        false,
      )
        .then((res) => {
          if (cancelled) return;
          const data = res?.data?.data;
          if (data) setPreview(data);
          else {
            setPreview(null);
            setError('Could not compute the schedule preview.');
          }
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    active,
    templateSlug,
    anchorDate,
    dayBasis,
    projectId,
    accessToken,
    companyId,
    overrideKey,
  ]);

  const scheduled = (preview?.tasks || []).filter((t) => t.deadlineDate);

  function setOverride(templateTaskId, value) {
    if (!editable) return;
    const rest = (overrides || []).filter(
      (o) => o.templateTaskId !== templateTaskId,
    );
    onOverridesChange(
      value ? [...rest, { templateTaskId, deadlineDate: value }] : rest,
    );
  }

  return (
    <div className={classes.section}>
      <div className={classes.header}>
        <p className={classes.title}>Computed schedule</p>
        {preview?.anchor && (
          <span className={classes.anchor}>
            From {preview.anchor.weekday}, {preview.anchor.date}
          </span>
        )}
      </div>

      {error && <div className={classes.error}>{error}</div>}

      {!error && loading && (
        <div className={classes.empty}>Computing schedule…</div>
      )}

      {!error && !loading && !templateSlug && (
        <div className={classes.empty}>
          Select a template to see its deadlines.
        </div>
      )}

      {!error && !loading && templateSlug && scheduled.length === 0 && (
        <div className={classes.empty}>
          This template has no tasks with a day offset, so no deadlines will be
          set.
        </div>
      )}

      {!error && !loading && scheduled.length > 0 && (
        <>
          <div className={classes.tableWrap}>
            <table className={classes.table}>
              <thead>
                <tr>
                  <th>Task</th>
                  <th>Offset</th>
                  <th>Deadline</th>
                </tr>
              </thead>
              <tbody>
                {scheduled.map((t, i) => (
                  <tr
                    key={t.templateTaskId || `${t.title}-${i}`}
                    className={t.overridden ? classes.overriddenRow : ''}
                  >
                    <td>{t.title}</td>
                    <td className={classes.offset}>+{t.noOfDays}</td>
                    <td>
                      {editable ? (
                        <div className={classes.editCell}>
                          <input
                            type="date"
                            className={classes.dateInput}
                            value={t.date || ''}
                            onChange={(e) =>
                              setOverride(t.templateTaskId, e.target.value)
                            }
                          />
                          <span className={classes.weekday}>{t.weekday}</span>
                          {t.overridden && (
                            <button
                              type="button"
                              className={classes.reset}
                              title={`Reset to computed date (${t.computedDate})`}
                              onClick={() => setOverride(t.templateTaskId, null)}
                            >
                              reset
                            </button>
                          )}
                          {!t.overridden && t.note && (
                            <span className={classes.note}>({t.note})</span>
                          )}
                        </div>
                      ) : (
                        <>
                          {t.weekday}, {t.date}
                          {t.note && (
                            <span className={classes.note}> ({t.note})</span>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className={classes.hint}>
            {dayBasis === 'business'
              ? 'Weekends and observed holidays are skipped.'
              : 'Calendar days are counted; a deadline landing on a weekend or holiday rolls to the next business day.'}
            {editable &&
              ' Edit any date to set it by hand — hand-set dates are kept when the schedule is recalculated.'}
          </p>
        </>
      )}
    </div>
  );
};

export default DeadlineSchedulePreview;
